import * as React from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, Checkbox, Chip, FormControlLabel, IconButton, Pagination, Stack, TablePagination, TableSortLabel, Tooltip, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import FmeRecordsReceiptModal from "../ReceiptsModal/FmeRecordsReceiptModal";
import DeleteReceiptModal from "../DeleteReceipts/DeleteReceiptModal";
import EditReceiptsModal from "../EditReceipts/EditReceiptsModal";
import Link from "@mui/material/Link";
import { useDispatch, useSelector } from 'react-redux';
import { formatNumber, formatPercent, formatDateToDDMMYYYY } from '../../../../../utils/helpers';
import { setPage, setRowsPerPage, setSort, setSelectedRow, setSelectedReceipt } from '../../../../../features/financeReceiptSlice';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { ReceiptStatus } from './ReceiptStatus';

interface Props {
  tableHeadings: { [key: string]: string };
}

const ReceiptsTableData = ({ tableHeadings }: Props) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const headCells = Object.entries(tableHeadings);
  const { receiptData, selectedRow, selectedCompany, selectedFME, selectedDate, selectedStatus, selectedReceiptNumber, selectedAccount, selectedPercentCovered, page, rowsPerPage, sort } = useSelector((state: any) => state.receipts);

  const renderBrands = (row: any) => {
    const allBrands = row?.fmes?.flatMap((fme: any) =>
      fme.brand_names
        ? fme.brand_names.split(',').map((brand: string) => brand.trim())
        : []
    ).filter(Boolean); // Filter out any empty strings from splitting or null/undefined
    const brands = [...new Set(allBrands)];

    if (brands.length === 0) return null;

    return (
      <Tooltip
        title={
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {brands.map((brand: any, index: number) => (
              <Typography key={index} variant="caption">
                {brand}
              </Typography>
            ))}
          </Box>
        }
        arrow
        placement="top"
      >
        <Box display="flex" alignItems="center" sx={{ cursor: "pointer" }}>
          <Chip
            label={brands[0] as string}
            sx={{
              backgroundColor: "#7B1FA2",
              color: "#ffffff",
              height: "26px",
            }}
          />
          {brands.length > 1 && (
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: "12px",
                backgroundColor: "#bdbdbd",
                ml: 0.5
              }}
            >
              +{brands.length - 1}
            </Avatar>
          )}
        </Box>
      </Tooltip>
    );
  };

  const renderFME = (row: any) => {
    const fmeIds = row?.fmes?.map((fme: any) => fme?.fme_id).filter((i: any) => i !== null && i !== undefined);
    const hasData = fmeIds && fmeIds.length > 0;
    const label = hasData ? fmeIds.map((id: any) => `${(id)}`).join(", ") : "N/A";

    const chip = (
      <Chip
        label={label}
        onClick={() => handleOpenModal(row)}
        sx={{
          backgroundColor: "#ffffff",
          color: "rgba(0, 0, 0, 0.87)",
          borderRadius: "5px",
          boxShadow: "1px 1px 2px 1px #00000030",
          height: "26px",
          fontWeight: "500",
          cursor: "pointer",
          border: "1px solid #ffffff",
          "&:hover": {
            backgroundColor: "transparent",
            color: "#9c27b0", // slightly darker on hover
            border: "1px solid #9c27b0",
          },
        }}
      />
    );

    if (!hasData) return chip;

    return (
      <Tooltip
        title={
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {fmeIds?.map((id: any, index: number) => (
              <Typography key={index} variant="caption">
                {id}
              </Typography>
            ))}
          </Box>
        }
        arrow
        placement="top"
      >
        {chip}
      </Tooltip>
    );
  };

  const renderAccount = (row: any) => {
    const accountIds = row?.fmes?.map((fme: any) => fme?.account_id).filter((i: any) => i !== null && i !== undefined);
    const hasData = accountIds && accountIds.length > 0;
    const label = hasData ? accountIds.map((id: any) => `${(id)}`).join(", ") : "N/A";

    const chip = (
      <Chip
        label={label}
        // onClick={() => handleOpenModal(row)}
        sx={{
          backgroundColor: "#ffffff",
          color: "rgba(0, 0, 0, 0.87)",
          borderRadius: "5px",
          boxShadow: "1px 1px 2px 1px #00000030",
          height: "26px",
          fontWeight: "500",
          cursor: "pointer",
          border: "1px solid #ffffff",
          "&:hover": {
            backgroundColor: "transparent",
            color: "#9c27b0", // slightly darker on hover
            border: "1px solid #9c27b0",
          },
        }}
      />
    );

    if (!hasData) return chip;

    return (
      <Tooltip
        title={
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {accountIds?.map((id: any, index: number) => (
              <Typography key={index} variant="caption">
                {id}
              </Typography>
            ))}
          </Box>
        }
        arrow
        placement="top"
      >
        {chip}
      </Tooltip>
    );
  };

  const handleChangePage = (_: any, newPage: number) => {
    dispatch(setPage(newPage));
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  const getSortedData = useCallback((arrayToSort: any[]) => {
    if (!sort.keytoSort || !sort.direction) {
      return arrayToSort;
    }

    return arrayToSort.slice().sort((a, b) => {
      const keytoSort = sort.keytoSort;

      if (keytoSort === undefined) {
        return 0;
      }

      let aValue = a[keytoSort];
      let bValue = b[keytoSort];

      // Handle nested FMES
      if (keytoSort === "fme_id") {
        aValue = a?.fmes?.[0]?.fme_id;
        bValue = b?.fmes?.[0]?.fme_id;
      }

      // Handle nested brand names
      if (keytoSort === "brand_names") {
        aValue = a?.fmes?.[0]?.brand_names;
        bValue = b?.fmes?.[0]?.brand_names;
      }

      // Handle nested account ids
      if (keytoSort === "account_id") {
        aValue = a?.fmes?.[0]?.account_id;
        bValue = b?.fmes?.[0]?.account_id;
      }

      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;

      if (aValue === "") return 1;
      if (bValue === "") return -1;

      // Special case: if sorting by date fields, compare as Date objects
      if (keytoSort === "modified" || keytoSort === "created" || keytoSort === "activity_date" || keytoSort === "receipt_date") {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        return sort.direction === "asc"
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      } else {
        return sort.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }
    });
  }, [sort]);

  const filteredData = useMemo(() => {
    const filtered = receiptData?.filter((receipt: any) => {
      const companyName = selectedCompany && selectedCompany?.length !== 0 ? selectedCompany?.includes(receipt?.finance_company_name) : true;
      const receiptNumber = selectedReceiptNumber && selectedReceiptNumber?.length !== 0 ? selectedReceiptNumber?.includes(receipt?.receipt_id) : true;
      const FMEIds = selectedFME && selectedFME?.length !== 0 ? receipt?.fmes?.some((fme:any) =>  selectedFME?.includes(String(fme?.fme_id))) : true;
      const receiptStatus = selectedStatus && selectedStatus?.length !== 0 ? selectedStatus?.includes(receipt?.receipt_status) : true;
      const accountName = selectedAccount && selectedAccount?.length !== 0 ? receipt?.fmes?.some((fme:any) =>  selectedAccount?.includes(String(fme?.account_id))) : true;
      // Percent Covered filter logic   
      const percentCovered = selectedPercentCovered && selectedPercentCovered?.length !== 0
        ? selectedPercentCovered?.some((range: string) => {
          const covered = Number(receipt?.total_covered);

          if (range === "Below 30%") {
            return covered < 30;
          } else if (range === "Between 30% and 70%") {
            return covered >= 30 && covered <= 70;
          } else if (range === "Over 70%") {
            return covered > 70;
          }
          return false;
        })
        : true;
      const isDateInRange =
        selectedDate?.length === 2 && selectedDate[0] && selectedDate[1]
          ? selectedDate[0] === selectedDate[1] // If both dates are the same, check for equality
            ? new Date(receipt?.receipt_date).toDateString() ===
            new Date(selectedDate[0]).toDateString()
            : new Date(receipt?.receipt_date) >= new Date(selectedDate[0]) &&
            new Date(receipt?.receipt_date) <= new Date(new Date(selectedDate[1]).setHours(23, 59, 59, 999))
          : true;
      return companyName && receiptNumber && FMEIds && receiptStatus && accountName && percentCovered && isDateInRange;
    })
    return getSortedData(filtered);
  }, [receiptData, selectedCompany, selectedAccount, selectedDate, selectedPercentCovered, selectedStatus, selectedReceiptNumber, selectedFME, getSortedData]);

  const tableHeaders = [
    { id: 0, KEY: "finance_company_name", label: "Company", sortable: true },
    { id: 1, KEY: "receipt_date", label: "Date", sortable: true },
    { id: 2, KEY: "receipt_id", label: "Receipt Number", sortable: true },
    { id: 3, KEY: "receipt_amount", label: "Receipt Amount", sortable: true },
    { id: 4, KEY: "receipt_status", label: "Receipt Status", sortable: true },
    { id: 5, KEY: "fme_id", label: "FME", sortable: true },
    { id: 6, KEY: "total_fmes_income", label: "FME Income", sortable: true },
    { id: 7, KEY: "total_policy", label: "Policy FME Income", sortable: true },
    { id: 8, KEY: "total_manual", label: "Manual FME Income", sortable: true },
    { id: 9, KEY: "total_covered", label: "% Covered", sortable: true },
    { id: 10, KEY: "account_id", label: "Account", sortable: true },
    { id: 11, KEY: "brand_names", label: "Brands", sortable: true },
  ];

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(setSort({ keytoSort: header.KEY, direction: header.KEY === sort.keytoSort && sort.direction === "asc" ? "desc" : "asc", }));
  };

  useEffect(() => {
    const currentPage = searchParams.get("page");
    if (currentPage !== null) {
      dispatch(setPage(Number(currentPage)));
    }
    // Otherwise reset to page 0 (when filters change)
    else {
      dispatch(setPage(0));
      // Also update URL to reflect page 0
      setSearchParams((prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);
        updatedParams.set("page", "0");
        return updatedParams;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, selectedCompany, selectedAccount, selectedReceiptNumber, selectedPercentCovered, selectedDate, selectedFME]);

  const handleSelectRow = (row: any) => {
    const updatedSelectedRows = selectedRow.includes(row) ? selectedRow.filter((r: any) => r !== row) : [...selectedRow, row];
    dispatch(setSelectedRow(updatedSelectedRows));
  }

  const [openModal, setOpenModal] = React.useState(false);

  const handleOpenModal = (row: any) => {
    setOpenModal(true);
    dispatch(setSelectedReceipt(row));
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    // setTimeout(() => {
    //   dispatch(setSelectedReceipt(null)); 
    // }, 1000);
  };

  const [deleteReceiptsOpenModal, setDeleteReceiptsOpenModal] =
    React.useState(false);
  const handleDeleteReceiptsOpenModal = () => {
    setDeleteReceiptsOpenModal(true);
  };
  const handleDeleteReceiptsCloseModal = () => {
    setDeleteReceiptsOpenModal(false);
  };

  const [editReceiptsOpenModal, setEditReceiptsOpenModal] =
    React.useState(false);
  const handleEditReceiptsOpenModal = () => {
    setEditReceiptsOpenModal(true);
  };
  const handleEditReceiptsCloseModal = () => {
    setEditReceiptsOpenModal(false);
  };

  return (
    <>
      <TableContainer className="receiptsTable receiptsMainTable">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.id}>
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ textAlign: "center" }}>
                <TableSortLabel>Quick Actions</TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData?.length > 0 && filteredData ? (
              filteredData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)?.map((row: any, index: any) => (
                <React.Fragment key={index}>
                  <TableRow
                    key={index}
                    selected={selectedRow.includes(index)}
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F0DEF3" : "white" }}>
                      <>
                        <FormControlLabel control={<Checkbox color="secondary" data-tracker="532" checked={selectedRow.includes(row)} onChange={() =>  handleSelectRow(row)} size="small" />}
                          label={
                            <Tooltip title={row?.finance_company_name || "N/A"}>
                              <span>{row?.finance_company_name || "N/A"}</span>
                            </Tooltip>
                          }
                        />
                      </>
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F0DEF3" : "white" }}>
                      {formatDateToDDMMYYYY(row?.receipt_date) || ""}
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F0DEF3" : "white" }}>
                      {row?.receipt_id || ""}
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F0DEF3" : "white" }}>
                      <Box display={"flex"} alignItems={"center"}>
                        {formatNumber(row?.receipt_amount || "")}
                        <Stack
                          sx={{
                            color: "rgba(0, 0, 0, 0.56)",
                            fontSize: "18px !important",
                            fontWeight: "400",
                            ml: 0.5
                          }}
                        >
                          {row?.receipt_currency_symbol || ""}
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F0DEF3" : "white" }}>
                      <ReceiptStatus status={row?.receipt_status} />
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F7EEF9" : "white" }}>
                      {renderFME(row)}
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F7EEF9" : "white" }}>
                      <Box display={"flex"} alignItems={"center"}>
                        {row?.total_fmes_income ? formatNumber(row?.total_fmes_income) : "N/A"}{" "}
                        <Stack
                          sx={{
                            color: "rgba(0, 0, 0, 0.56)",
                            fontSize: "18px !important",
                            fontWeight: "400",
                            ml: 0.5
                          }}
                        >
                          {row?.receipt_currency_symbol || ""}
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F7EEF9" : "white" }}>
                      <Box display={"flex"} alignItems={"center"}>
                        {row?.total_policy ? formatNumber(row?.total_policy) : "N/A"}{" "}
                        <Stack
                          sx={{
                            color: "rgba(0, 0, 0, 0.56)",
                            fontSize: "18px !important",
                            fontWeight: "400",
                            ml: 0.5
                          }}
                        >
                          {row?.receipt_currency_symbol || ""}
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F7EEF9" : "white" }}>
                      <Box display={"flex"} alignItems={"center"}>
                        {row?.total_manual ? formatNumber(row?.total_manual) : 0}{" "}
                        <Stack
                          sx={{
                            color: "rgba(0, 0, 0, 0.56)",
                            fontSize: "18px !important",
                            fontWeight: "400",
                            ml: 0.5
                          }}
                        >
                          {row?.receipt_currency_symbol || ""}
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F7EEF9" : "white" }}>
                      {row?.total_covered ? formatPercent(row?.total_covered) : ""}
                    </TableCell>
                    <TableCell align="left" sx={{ backgroundColor: selectedRow.includes(row) ? "#F7EEF9" : "white" }}>
                      {renderAccount(row)}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: selectedRow.includes(row) ? "#F7EEF9" : "white" }}>
                      <div className="chip-data">
                        {renderBrands(row)}
                      </div>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", backgroundColor: selectedRow.includes(row) ? "#F0DEF3" : "white" }}>
                      <Tooltip title="Pair" placement="top">
                        <Link 
                        // href="/finance/providers_expenses/receipts/receipts_pairing"
                        >
                          <IconButton data-tracker="533" disabled sx={{ width: "40px", height: "40px" }}>
                            <InsertLinkIcon
                              color="secondary"
                              fontSize="medium"
                            />
                          </IconButton>
                        </Link>
                      </Tooltip>
                      <Tooltip title="Edit" placement="top">
                        <IconButton data-tracker="534" disabled sx={{ width: "40px", height: "40px", mx: 0.5 }}
                          onClick={handleEditReceiptsOpenModal}
                        >
                          <EditOutlinedIcon
                            color="secondary"
                            fontSize="medium"
                          />
                        </IconButton>
                      </Tooltip>
                      <EditReceiptsModal
                        open={editReceiptsOpenModal}
                        handleClose={handleEditReceiptsCloseModal}
                      />
                      <Tooltip title="Delete" placement="top">
                        <IconButton data-tracker="535" disabled sx={{ width: "40px", height: "40px" }} onClick={handleDeleteReceiptsOpenModal}>
                          <DeleteOutlineOutlinedIcon
                            color="secondary"
                            fontSize="medium"
                          />
                        </IconButton>
                      </Tooltip>
                      <DeleteReceiptModal
                        open={deleteReceiptsOpenModal}
                        handleClose={handleDeleteReceiptsCloseModal}
                      />
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <>
                <TableRow sx={{ "& > *": { borderBottom: "1px solid rgba(224, 224, 224, 1) !important" } }}>
                  <TableCell colSpan={headCells?.length} align="center">
                    <Typography variant="body1">
                      No Data Available
                    </Typography>
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={filteredData?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
            {
              display: "none",
            },
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-toolbar": {
              paddingLeft: "16px",
            },
          }}
        />
        <Pagination
          count={Math.ceil(filteredData?.length / rowsPerPage)}
          page={page + 1}
          showFirstButton
          showLastButton
          className="pagination-button"
          color="secondary"
          size="small"
          sx={{
            ".css-wjh20t-MuiPagination-ul": {
              justifyContent: "center",
            },
          }}
          onChange={(_, value) => handleChangePage(null, value - 1)}
        />
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredData?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          classes={{
            displayedRows: "hidden-displayed-rows", // Apply the custom class
          }}
          sx={{
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-input": {
              marginRight: "15px",
            },
          }}
        />
      </Stack>
      <FmeRecordsReceiptModal open={openModal} handleClose={handleCloseModal} />
    </>
  );
};

export default ReceiptsTableData;
